import { useState } from 'react';

import useToast from '@/components/Toast/useToast';
import { Tooltip } from '@/design-system/components';
import { Button } from '@/design-system/components/Button';
import { DatasetSchema, ResourceTag } from '@/types/schema';

import { Pane } from '../Pane';
import { FileUploadTemplateForm } from './NewDatasetCrudForm';
import { useFileUploadTemplateStore } from './hooks/useFileUploadTemplateStore';
import { useUploadFile } from './hooks/useUploadFile';

interface UploadExamplesToExistingDatasetPaneProps {
  open: boolean;
  onClose: () => void;
  dataset: DatasetSchema;
  onSuccess?: () => void;
}

export function UploadExamplesToExistingDatasetPane({
  open,
  onClose,
  dataset,
  onSuccess,
}: UploadExamplesToExistingDatasetPaneProps) {
  const [tagsOnResource, setTagsOnResource] = useState<ResourceTag[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const { createToast } = useToast();

  const { uploadToExistingDataset } = useUploadFile();
  const fileName = useFileUploadTemplateStore((state) => state.fileName);
  const inputKeys = useFileUploadTemplateStore((state) => state.inputKeys);
  const uploadError = useFileUploadTemplateStore((state) => state.error);
  const clearStore = useFileUploadTemplateStore((state) => state.onCancel);

  const handleUpload = async () => {
    if (!fileName || !inputKeys?.length) {
      createToast({
        title: 'Missing Information',
        description: 'Please upload a file and select at least one input key.',
        error: true,
      });
      return;
    }

    setIsUploading(true);
    try {
      await uploadToExistingDataset(dataset.id);
      createToast({
        title: 'Examples Added Successfully',
        description: `Examples from ${fileName} have been added to ${dataset.name}.`,
      });
      clearStore();
      onSuccess?.();
      onClose();
    } catch (error) {
      createToast({
        title: 'Upload Failed',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred.',
        error: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      clearStore();
      onClose();
    }
  };

  let submitDisabledMessage;
  if (uploadError) {
    submitDisabledMessage = uploadError;
  } else if (isUploading) {
    submitDisabledMessage = 'Upload in progress...';
  } else if (!fileName) {
    submitDisabledMessage = 'Please upload a file';
  } else if (!inputKeys?.length) {
    submitDisabledMessage = 'Please select at least one input key';
  }

  const submitButton = (
    <Button
      variant="normal"
      color="primary"
      size="md"
      loading={isUploading}
      disabled={!!submitDisabledMessage}
      onClick={handleUpload}
      data-testid="add-examples-submit"
    >
      {isUploading ? 'Adding Examples...' : 'Add Examples'}
    </Button>
  );

  const cancelAndSubmitButtons = (
    <div className="mr-4 flex items-center gap-2">
      <Button
        onClick={handleClose}
        size="md"
        color="secondary"
        variant="plain"
        disabled={isUploading}
      >
        Cancel
      </Button>
      <Tooltip title={submitDisabledMessage}>
        <div className="items-stretch">{submitButton}</div>
      </Tooltip>
    </div>
  );

  return (
    <Pane
      open={open}
      onClose={handleClose}
      title={`Add Examples to ${dataset.name}`}
      topBarRightElement={cancelAndSubmitButtons}
    >
      <FileUploadTemplateForm
        isEditing={false}
        mode="addToExisting"
        tagsOnResource={tagsOnResource}
        setTagsOnResource={setTagsOnResource}
      />
    </Pane>
  );
}
